import { useState, useEffect, type FormEvent } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import Dropdown from '../../../ui-components/Dropdown';

import {
  useAddNewPermissionMutation,
  useLazyPermissionGroupQuery,
} from './permissionApi';
import { setPermissionGroup } from './permissionSlice';

import type { PermissionGroup } from './model';

const PermissionForm = () => {
  const [permissionGroupApi] = useLazyPermissionGroupQuery();
  const [addNewPermissionApi] = useAddNewPermissionMutation();

  const [groupOptions, setGroupOptions] = useState<
    PermissionGroup[] | undefined
  >(undefined);
  const [permissionOptions, setPermissionOptions] = useState<PermissionGroup[]>(
    [
      { label: 'List', value: 'list' },
      { label: 'Add', value: 'add' },
      { label: 'Update', value: 'update' },
      { label: 'View', value: 'view' },
      { label: 'Change status', value: 'change_status' },
      { label: 'Delete', value: 'delete' },
    ]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [group, setGroup] = useState<string>('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleCancel = () => {
    setGroup('');
    setPermissions([]);
    navigate(-1);
  };

  useEffect(() => {
    async function load() {
      try {
        const permissionRes = await permissionGroupApi().unwrap();
        const data = (permissionRes?.data as any)?.data?.records || [];
        setGroupOptions(data);
        dispatch(setPermissionGroup(data));
      } catch {
        // Handled by baseQuery global toast
      }
    }
    load();
  }, [dispatch, permissionGroupApi]);

  const generatedCodes: string[] =
    group && permissions.length > 0
      ? permissions.map(
          (p) =>
            `${group.toLowerCase().replace(/\s+/g, '_')}_${p
              .toLowerCase()
              .replace(/\s+/g, '_')}`
        )
      : [];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (permissions.length !== 0 && group.trim() !== '') {
      try {
        const obj = {
          name: group, // Assuming the API expects name and code
          code: group.toLowerCase().replace(/\s+/g, '_'),
          permissions,
        };
        await addNewPermissionApi(obj as any).unwrap();
        navigate('/dashboard');
      } catch (err: any) {
        console.error(err);
      }
    }
  };

  return (
    <div className="w-full bg-[#f9fafb] dark:bg-gray-900">
      <div className="flex flex-col card dark:bg-gray-800 gap-6 h-[calc(100vh-9rem)]">
        <h2 className="top-title">Manage Permissions</h2>
        <div className="overflow-y-auto bg-white rounded-lg dark:bg-gray-800 dark:border-gray-700 p-6 shadow border border-gray-200 h-dvh">
          <div className="max-w-1/2 w-full">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Dropdown
                  label="Permission Group"
                  value={group}
                  setValue={(val) => setGroup(String(val))}
                  options={groupOptions as any}
                  setOptions={setGroupOptions as any}
                  labelKey="groupName"
                  valueKey="groupCode"
                  isMulti={false}
                  required={true}
                  placeholder="Permission Group"
                  isSearchable={true}
                  isCreatable={true}
                  isFocusAllowed={true}
                />
                {formSubmitted && group === '' && (
                  <p className="error-msg">
                    Please enter a value for the Group.
                  </p>
                )}
              </div>

              <div>
                <Dropdown
                  label="Permissions"
                  value={permissions}
                  setValue={(val) => setPermissions(val as string[])}
                  options={permissionOptions as any}
                  setOptions={setPermissionOptions as any}
                  labelKey="label"
                  valueKey="value"
                  isMulti={true}
                  required={true}
                  placeholder="Permissions"
                  isSearchable={true}
                  isCreatable={true}
                />
                {formSubmitted && permissions.length === 0 && (
                  <p className="error-msg">
                    Please enter a value for the Permissions.
                  </p>
                )}
              </div>

              {generatedCodes.length > 0 && (
                <div>
                  <label
                    htmlFor="generatedCodes"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    Generated Permission Code
                  </label>
                  <div id="generatedCodes" className="space-y-1">
                    {generatedCodes.map((code) => (
                      <p
                        key={code}
                        className="text-gray-600 dark:text-gray-300"
                      >
                        {code}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionForm;
